import {
  EEventContextAction,
  EEventType,
  ELegalDocumentKind,
  type ILegalDocumentVersion,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { getHead } from '../../head';
import { getCurrentVersion, getVersion } from '../../legal/legal-versions';
import {
  type IPrivacyResourceReader,
  readPrivacyContent,
  resolvePrivacyContent,
} from '../../legal/privacy-content';
import { buildInterpolationValues } from '../../legal/terms-content';
import PrivacyDocument from '../components/legal/privacy-document';
import { eventSend } from '../reducers/event/reducer';
import { type ILegalState } from '../reducers/legal/types';
import { setActiveCategory } from '../reducers/products/reducer';

function PrivacyPolicyContainer({
  dispatch,
  Legal,
}: {
  dispatch: Dispatch;
  Legal: ILegalState;
}) {
  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_PRIVACY_POLICY,
        action: EEventContextAction.ENTER,
      }),
    );
    dispatch(
      setActiveCategory({
        category: {
          uuid: '',
        },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_PRIVACY_POLICY,
          action: EEventContextAction.LEAVE,
        }),
      );
    };
  }, [dispatch]);

  const { language = 'en', versionCode: requestedVersionCode } = useParams<{
    language: string;
    versionCode?: string;
  }>();

  const currentVersionCode: string =
    Legal.privacyPolicy?.versionCode ??
    getCurrentVersion(ELegalDocumentKind.PRIVACY_POLICY).versionCode;
  const displayedVersion: ILegalDocumentVersion | null = getVersion(
    ELegalDocumentKind.PRIVACY_POLICY,
    requestedVersionCode ?? currentVersionCode,
  );
  const privacyNamespace: string = displayedVersion?.namespace ?? 'customer';

  const { t, i18n, ready } = useTranslation([privacyNamespace, 'customer'], {
    nsMode: 'fallback',
  });

  useEffect((): void => {
    const { title } = getHead(t, t('privacyPolicy.title'));
    document.title = title;
  }, [t]);

  if (!ready || !displayedVersion) {
    return <div className="container container--t terms" />;
  }

  const reader: IPrivacyResourceReader = i18n;
  const content = readPrivacyContent(
    reader,
    language.toLowerCase(),
    privacyNamespace,
  );
  const isArchived: boolean =
    displayedVersion.versionCode !== currentVersionCode;

  return (
    <div className="container container--t terms">
      {isArchived ? (
        <p className="terms--archived" role="note">
          {t('legal.archivedVersionBanner')}{' '}
          <Link to={`/${language}/privacy-policy`}>
            {t('legal.showCurrentVersion')}
          </Link>
        </p>
      ) : null}

      {content ? (
        <PrivacyDocument
          content={resolvePrivacyContent(
            content,
            buildInterpolationValues(Legal.seller, {
              versionCode: displayedVersion.versionCode,
            }),
          )}
        />
      ) : null}
    </div>
  );
}

const mapStateToProps = ({ Legal }: { Legal: ILegalState }) => ({
  Legal,
});

export default connect(mapStateToProps)(PrivacyPolicyContainer);
